import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  Input,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  listSubscriptionsAction,
  upsertSubscriptionAction,
  deleteSubscriptionAction,
} from "../../../store/company/companyActions";
import { dateFormat } from "../../shared/formatting";

const { Text } = Typography;

const emptySubscription = {
  CompanySubscriptionKey: null,
  CompanySubscriptionTypeKey: null,
  PricingModelKey: null,
  IsActive: true,
  EffectiveDate: null,
  Notes: "",
};

const CompanySubscriptionsPanel = () => {
  const dispatch = useDispatch();
  const companyKey = useSelector(
    (state) => state.actor?.details?.data?.BrokerId,
  );
  const { data: subscriptions = [], subscriptionTypes = [], pricingModels = [], loading = false } =
    useSelector((state) => state.company?.subscriptions || {});

  const [editingKey, setEditingKey] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);

  useEffect(() => {
    if (companyKey) {
      dispatch(listSubscriptionsAction(companyKey));
    }
  }, [companyKey, dispatch]);

  // Filter pricing models by selected subscription type
  const filteredPricingModels = useMemo(() => {
    if (!editingRecord?.CompanySubscriptionTypeKey) return [];
    return pricingModels.filter(
      (pm) => pm.CompanySubscriptionTypeKey === editingRecord.CompanySubscriptionTypeKey,
    );
  }, [editingRecord?.CompanySubscriptionTypeKey, pricingModels]);

  // Subscription types already assigned (exclude current editing row)
  const usedSubscriptionTypeKeys = useMemo(() => {
    return subscriptions
      .filter((s) => s.CompanySubscriptionKey !== editingKey)
      .map((s) => s.CompanySubscriptionTypeKey);
  }, [subscriptions, editingKey]);

  const handleAdd = () => {
    setEditingKey("new");
    setEditingRecord({ ...emptySubscription });
  };

  const handleEdit = (record) => {
    setEditingKey(record.CompanySubscriptionKey);
    setEditingRecord({ ...record });
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditingRecord(null);
  };

  const handleSave = () => {
    if (!editingRecord.CompanySubscriptionTypeKey) return;
    dispatch(
      upsertSubscriptionAction({
        ...editingRecord,
        CompanyKey: companyKey,
      }),
    );
    setEditingKey(null);
    setEditingRecord(null);
  };

  const handleDelete = (record) => {
    dispatch(
      deleteSubscriptionAction({
        CompanySubscriptionKey: record.CompanySubscriptionKey,
        CompanyKey: companyKey,
      }),
    );
  };

  const handleFieldChange = (field, value) => {
    setEditingRecord((prev) => {
      const updated = { ...prev, [field]: value };
      // Clear pricing model when subscription type changes
      if (field === "CompanySubscriptionTypeKey") {
        updated.PricingModelKey = null;
      }
      return updated;
    });
  };

  const isEditing = (record) => {
    if (editingKey === "new") {
      return record.CompanySubscriptionKey === null;
    }
    return record.CompanySubscriptionKey === editingKey;
  };

  const columns = [
    {
      title: "Service",
      dataIndex: "SubscriptionTypeName",
      key: "SubscriptionTypeName",
      width: 180,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Select
              size="small"
              style={{ width: "100%" }}
              value={editingRecord?.CompanySubscriptionTypeKey}
              onChange={(v) => handleFieldChange("CompanySubscriptionTypeKey", v)}
              placeholder="Select service"
              options={subscriptionTypes.map((st) => ({
                value: st.CompanySubscriptionTypeKey,
                label: st.SubscriptionTypeName,
                disabled: usedSubscriptionTypeKeys.includes(st.CompanySubscriptionTypeKey),
              }))}
            />
          );
        }
        return text;
      },
    },
    {
      title: "Pricing Model",
      dataIndex: "PricingModelName",
      key: "PricingModelName",
      width: 260,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Select
              size="small"
              style={{ width: "100%" }}
              value={editingRecord?.PricingModelKey}
              onChange={(v) => handleFieldChange("PricingModelKey", v)}
              placeholder="Select pricing model"
              allowClear
              disabled={!editingRecord?.CompanySubscriptionTypeKey}
              options={filteredPricingModels.map((pm) => ({
                value: pm.PricingModelKey,
                label: pm.PricingModelName,
              }))}
            />
          );
        }
        return text ?? <Text type="secondary">�</Text>;
      },
    },
    {
      title: "Status",
      dataIndex: "IsActive",
      key: "IsActive",
      width: 100,
      render: (value, record) => {
        if (isEditing(record)) {
          return (
            <Switch
              size="small"
              checked={editingRecord?.IsActive}
              onChange={(v) => handleFieldChange("IsActive", v)}
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          );
        }
        return value ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        );
      },
    },
    {
      title: "Effective Date",
      dataIndex: "EffectiveDate",
      key: "EffectiveDate",
      width: 140,
      render: (value, record) => {
        if (isEditing(record)) {
          return (
            <Input
              type="date"
              size="small"
              value={
                editingRecord?.EffectiveDate
                  ? dayjs(editingRecord.EffectiveDate).format("YYYY-MM-DD")
                  : ""
              }
              onChange={(e) =>
                handleFieldChange("EffectiveDate", e.target.value || null)
              }
            />
          );
        }
        return value ? (
          dayjs(value).format(dateFormat[0])
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "Notes",
      key: "Notes",
      ellipsis: true,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Input
              size="small"
              value={editingRecord?.Notes ?? ""}
              maxLength={500}
              placeholder="Enter notes"
              onChange={(e) => handleFieldChange("Notes", e.target.value)}
            />
          );
        }
        return text || null; 
      },
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, record) => {
        if (isEditing(record)) {
          return (
            <Space size={4}>
              <Button
                size="small"
                type="link"
                icon={<SaveOutlined />}
                onClick={handleSave}
                disabled={!editingRecord?.CompanySubscriptionTypeKey}
              />
              <Button
                size="small"
                type="link"
                icon={<UndoOutlined />}
                onClick={handleCancel}
              />
            </Space>
          );
        }
        return (
          <Space size={4}>
            <Button
              size="small"
              type="link"
              icon={<EditOutlined />}
              disabled={editingKey != null}
              onClick={() => handleEdit(record)}
            />
            <Popconfirm
              title="Delete this subscription?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                size="small"
                type="link"
                danger
                icon={<DeleteOutlined />}
                disabled={editingKey != null}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Build table data: existing subscriptions + new row if adding
  const tableData = useMemo(() => {
    const rows = subscriptions.map((s) => ({
      ...s,
      key: s.CompanySubscriptionKey ?? `existing-${Math.random()}`,
    }));
    if (editingKey === "new") {
      rows.push({ 
        ...emptySubscription, 
        key: "new",
        CompanySubscriptionKey: null  // Ensure this is null for new row
      });
    }
    return rows;
  }, [subscriptions, editingKey]);

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={editingKey != null}
        >
          Add Service
        </Button>
        <Table
          size="small"
          columns={columns}
          dataSource={tableData}
          pagination={false}
          bordered
          locale={{ emptyText: "No subscriptions assigned" }}
        />
      </Space>
    </Spin>
  );
};

export default CompanySubscriptionsPanel;